import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { Header } from "../components/Header";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, ShieldCheck, Download, Clock } from "lucide-react";

export default function ExamAttempt() {
  const { courseId, attemptId } = useParams();
  const { t, lang, isRTL } = useI18n();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> key
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startedRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.getAttempt(attemptId).then((a) => {
      setAttempt(a);
      const map = {};
      (a.answers || []).forEach((x) => (map[x.question_id] = x.selected_key));
      setAnswers(map);
      startedRef.current = new Date(a.started_at).getTime();
      if (a.status === "submitted") {
        setResult({
          passed: a.passed,
          score_correct: a.score_correct,
          score_total: a.score_total,
          score_percent: a.score_percent,
          duration_seconds: a.duration_seconds,
          questions: a.questions,
        });
      }
    });
  }, [attemptId]);

  useEffect(() => {
    if (result) return;
    const iv = setInterval(() => {
      if (startedRef.current) {
        setElapsed(Math.floor((Date.now() - startedRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [result]);

  async function saveAnswer(qid, key) {
    setAnswers((prev) => ({ ...prev, [qid]: key }));
    try {
      await api.answerQuestion(attemptId, qid, key);
    } catch {
      // silent — kids can retry
    }
  }

  async function submit() {
    setSubmitting(true);
    setConfirmOpen(false);
    try {
      const r = await api.submitAttempt(attemptId);
      setResult(r);
      if (r.passed) {
        confetti({
          particleCount: 220,
          spread: 130,
          origin: { y: 0.3 },
          colors: ["#4361EE", "#FFB703", "#F72585", "#4CC9F0", "#166534"],
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!attempt) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24">
          <div className="h-64 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
        </div>
      </div>
    );
  }

  // ==== Result screen ====
  if (result) {
    return (
      <ResultView
        result={result}
        courseId={courseId}
        t={t}
        lang={lang}
        onCopyVerify={async (certId) => {
          const url = `${window.location.origin}/verify/${certId}`;
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
          } catch { /* clipboard blocked; silently ignore */ }
        }}
        copied={copied}
      />
    );
  }

  // ==== Runner ====
  const total = attempt.questions.length;
  const q = attempt.questions[idx];
  const selected = answers[q.question_id];
  const canGoNext = idx < total - 1;
  const canGoPrev = idx > 0;
  const answeredCount = Object.keys(answers).length;
  const ArrowFwd = isRTL ? ArrowLeft : ArrowRight;
  const ArrowBack = isRTL ? ArrowRight : ArrowLeft;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="exam-attempt-page">
      <Header />
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-8 md:py-10">
        <div className="flex items-center justify-between text-sm font-bold text-black/60">
          <div>
            {t("exam.question")} <span className="text-[#1A1A1A]">{idx + 1}</span> {t("exam.of")} {total}
          </div>
          <div className="inline-flex items-center gap-1" data-testid="exam-timer">
            <Clock className="h-4 w-4" /> {mm}:{ss}
          </div>
        </div>
        <div className="mt-2 h-2 rounded-full bg-black/5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#4361EE] to-[#4CC9F0] transition-all"
            style={{ width: `${((idx + 1) / total) * 100}%` }}
          />
        </div>

        <div className="mt-8 rounded-3xl bg-white border-2 border-black/5 p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="exam-question-card">
          <div className="text-xs font-black uppercase tracking-wider text-black/50">
            {t("exam.question")} {idx + 1}
          </div>
          <h2 className="mt-2 text-2xl md:text-3xl font-black leading-snug" data-testid="exam-prompt">
            {q.prompt[lang] || q.prompt.en}
          </h2>

          <div className="mt-6 space-y-2">
            {q.choices.map((c) => {
              const isSel = selected === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => saveAnswer(q.question_id, c.key)}
                  className={`w-full text-start flex items-center gap-3 rounded-2xl border-2 px-4 py-3 transition ${
                    isSel
                      ? "border-[#4361EE] bg-[#EEF2FF] shadow-[0_3px_0_#2A3EB1]"
                      : "border-black/10 bg-white hover:border-black/30"
                  }`}
                  data-testid={`exam-choice-${c.key}`}
                >
                  <span
                    className={`h-8 w-8 grid place-items-center rounded-full font-black text-sm ${
                      isSel ? "bg-[#4361EE] text-white" : "bg-black/5 text-black/70"
                    }`}
                  >
                    {c.key.toUpperCase()}
                  </span>
                  <span className="flex-1 font-semibold text-[#1A1A1A]">
                    {c.text[lang] || c.text.en}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={!canGoPrev}
            className="rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-5 py-2.5 text-sm transition disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="exam-prev-btn"
          >
            <ArrowBack className="h-4 w-4" /> {t("exam.prev")}
          </button>
          <div className="text-xs font-bold text-black/50 ms-auto me-2">
            {answeredCount}/{total}
          </div>
          {canGoNext ? (
            <button
              onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
              className="rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-2.5 text-sm shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition inline-flex items-center gap-2"
              data-testid="exam-next-btn"
            >
              {t("exam.next")} <ArrowFwd className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={submitting}
              className="rounded-full bg-[#166534] text-white font-bold px-5 py-2.5 text-sm shadow-[0_3px_0_#0E4022] hover:translate-y-[1px] hover:shadow-[0_2px_0_#0E4022] transition"
              data-testid="exam-submit-btn"
            >
              {t("exam.submitCta")}
            </button>
          )}
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="exam-submit-confirm">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmOpen(false)} />
          <div className="relative w-full max-w-sm rounded-[2rem] bg-white p-6 border-2 border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.30)]">
            <h3 className="text-2xl font-black">{t("exam.submitConfirmTitle")}</h3>
            <p className="mt-2 text-sm text-black/70">{t("exam.submitConfirmBody")}</p>
            <div className="mt-6 flex gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="flex-1 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-4 py-3 text-sm"
                data-testid="exam-submit-cancel"
              >
                {t("exam.submitConfirmNo")}
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="flex-1 rounded-full bg-[#166534] text-white font-bold px-4 py-3 text-sm shadow-[0_3px_0_#0E4022] disabled:opacity-60"
                data-testid="exam-submit-yes"
              >
                {t("exam.submitConfirmYes")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultView({ result, courseId, t, lang, onCopyVerify, copied }) {
  const passed = result.passed;
  const cert = result.certificate;
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="exam-result-page">
      <Header />
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-12">
        <div
          className={`rounded-[2.5rem] p-8 md:p-10 border-2 ${
            passed
              ? "bg-gradient-to-br from-[#DCFCE7] to-[#DFF6FD] border-[#166534]/20"
              : "bg-gradient-to-br from-[#FEE2E2] to-[#FEF3C7] border-[#991B1B]/20"
          }`}
        >
          <div
            className={`inline-flex items-center gap-2 rounded-full text-white font-black px-3 py-1 text-xs uppercase tracking-wider ${
              passed ? "bg-[#166534]" : "bg-[#991B1B]"
            }`}
            data-testid="exam-result-badge"
          >
            {passed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
            {passed ? t("exam.passed") : t("exam.failed")}
          </div>
          <h1 className="mt-4 text-5xl md:text-6xl font-black tracking-tight" data-testid="exam-result-score">
            {result.score_percent}%
          </h1>
          <p className="mt-2 text-black/70 font-bold">
            {result.score_correct} / {result.score_total} · {t("exam.threshold")}: 70%
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {passed && cert ? (
              <>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${cert.pdf_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-3 shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition text-sm"
                  data-testid="result-download-cert"
                >
                  <Download className="h-4 w-4" /> {t("exam.downloadCert")}
                </a>
                <button
                  onClick={() => onCopyVerify(cert.certificate_id)}
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-5 py-3 text-sm transition"
                  data-testid="result-share-verify"
                >
                  <ShieldCheck className="h-4 w-4" /> {copied ? t("exam.copiedToast") : t("exam.shareVerify")}
                </button>
              </>
            ) : (
              <a
                href={`/courses/${courseId}/exam`}
                className="inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-bold px-5 py-3 shadow-[0_3px_0_#2A3EB1] text-sm"
                data-testid="result-retake"
              >
                {t("exam.retakeCta")}
              </a>
            )}
          </div>
        </div>

        <h2 className="mt-10 text-2xl md:text-3xl font-black">{t("exam.review")}</h2>
        <ul className="mt-4 space-y-3" data-testid="exam-review-list">
          {result.questions.map((q, i) => (
            <li
              key={q.question_id}
              className={`rounded-3xl bg-white border-2 p-5 ${
                q.is_correct ? "border-[#166534]/25" : "border-[#991B1B]/25"
              }`}
              data-testid={`review-q-${i + 1}`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`h-7 w-7 rounded-full grid place-items-center flex-shrink-0 ${
                    q.is_correct ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"
                  }`}
                >
                  {q.is_correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[#1A1A1A]">{q.prompt[lang] || q.prompt.en}</div>
                  <div className="mt-2 text-sm">
                    {q.choices.map((c) => {
                      const isCorrect = c.key === q.correct_key;
                      const isSelected = c.key === q.selected_key;
                      return (
                        <div
                          key={c.key}
                          className={`flex items-center gap-2 px-2 py-1 rounded ${
                            isCorrect
                              ? "text-[#166534] font-bold"
                              : isSelected
                              ? "text-[#991B1B] font-bold line-through"
                              : "text-black/60"
                          }`}
                        >
                          <span className="w-5 text-xs font-black">{c.key.toUpperCase()}.</span>
                          <span>{c.text[lang] || c.text.en}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 rounded-2xl bg-[#F3F0E9] px-4 py-3 text-sm text-[#1A1A1A]">
                    <span className="font-black text-black/60 uppercase text-[10px] tracking-wider">
                      {t("exam.explanation")}:{" "}
                    </span>
                    {q.explanation[lang] || q.explanation.en}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-10">
          <a
            href={`/courses/${courseId}`}
            className="text-sm font-bold text-black/60 hover:text-[#4361EE]"
            data-testid="result-back-to-course"
          >
            ← {t("exam.backToCourse")}
          </a>
        </div>
      </div>
    </div>
  );
}
